// ═══════════════════════════════════════════════════════════════
// HotWash — Dashboard
// Overview stats, AI summary, recent observations, incident list
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Badge, Stack, Button,
  RingProgress, Center, Skeleton,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';
import {
  IconPlus, IconArrowRight,
} from '@tabler/icons-react';
import { useHotWashStore } from '../../store/hotwashStore';
import { useAuthStore } from '../../store/authStore';
import { TIER_META, TIER_KEYS } from '../../types';
import type { Observation } from '../../types';
import AISummaryPanel from '../../components/hotwash/AISummaryPanel';
import ObservationModal from '../../components/hotwash/ObservationModal';
import { semantic } from '../../lib/colors';

export default function HotWashDashboard() {
  const navigate = useNavigate();
  const { observations, loading: isLoading, loadObservations, deleteObservation } = useHotWashStore();
  const { activeIncident } = useAuthStore();

  const [selectedObs, setSelectedObs] = useState<Observation | null>(null);
  const [obsModalOpened, { open: openObsModal, close: closeObsModal }] = useDisclosure(false);

  useEffect(() => { loadObservations(); }, [loadObservations]);

  const incObs = activeIncident
    ? observations.filter(o => String(o.incidentId) === String(activeIncident.id))
    : observations;

  const tierCounts = TIER_KEYS.map(tier => ({
    tier,
    count: incObs.filter(o => o.tier === tier).length,
    ...TIER_META[tier],
  }));

  const total = incObs.length;
  const withRemedy = incObs.filter(o => o.suggestedRemedy).length;
  const remedyPct = total > 0 ? Math.round((withRemedy / total) * 100) : 0;

  const recent = [...incObs]
    .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''))
    .slice(0, 5);

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">HotWash — After-Action Review</Title>
          <Text size="sm" c="dimmed">
            {activeIncident
              ? `Viewing: ${activeIncident.name}`
              : `All incidents — ${observations.length} total observations`}
          </Text>
        </div>
        <Button leftSection={<IconPlus size={14} />} onClick={() => navigate('/hotwash/submit')}>
          Submit Observation
        </Button>
      </Group>

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          {[1,2,3,4].map(i => <Skeleton key={i} height={120} radius="sm" />)}
        </SimpleGrid>
      ) : (
        <>
          {/* AI Summary Panel */}
          {incObs.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <AISummaryPanel observations={incObs} />
            </div>
          )}

          {/* Tier Stat Cards */}
          <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
            {tierCounts.map(t => (
              <Paper key={t.tier} p="sm" radius="sm" withBorder ta="center"
                style={{ borderTop: `3px solid ${t.color}` }}>
                <Text size="xs" c="dimmed">{t.label}</Text>
                <Text fw={700} fz={28} style={{ color: t.color }}>{t.count}</Text>
              </Paper>
            ))}
          </SimpleGrid>

          {/* Summary Row */}
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            {/* Remedy Coverage */}
            <Paper p="md" radius="sm" withBorder>
              <Group>
                <RingProgress
                  size={80}
                  thickness={8}
                  roundCaps
                  sections={[{ value: remedyPct, color: remedyPct > 70 ? 'var(--color-success)' : remedyPct > 40 ? 'var(--color-warning)' : semantic.error }]}
                  label={<Center><Text fw={700} size="sm">{remedyPct}%</Text></Center>}
                />
                <div>
                  <Text fw={600}>Remedy Coverage</Text>
                  <Text size="sm" c="dimmed">
                    {withRemedy} of {total} observations have a suggested remedy
                  </Text>
                </div>
              </Group>
            </Paper>

            {/* Quick Stats */}
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} mb="xs">Quick Stats</Text>
              <SimpleGrid cols={3}>
                <div>
                  <Text fz={24} fw={700} style={{ color: 'var(--navy)' }}>{total}</Text>
                  <Text size="xs" c="dimmed">Total</Text>
                </div>
                <div>
                  <Text fz={24} fw={700} style={{ color: semantic.error }}>
                    {tierCounts.find(t => t.tier === 'critical_error')?.count ?? 0}
                  </Text>
                  <Text size="xs" c="dimmed">Critical</Text>
                </div>
                <div>
                  <Text fz={24} fw={700} style={{ color: semantic.success }}>
                    {new Set(incObs.map(o => o.category)).size}
                  </Text>
                  <Text size="xs" c="dimmed">Categories</Text>
                </div>
              </SimpleGrid>
            </Paper>
          </SimpleGrid>

          <SimpleGrid cols={1}>
            {/* Recent Observations */}
            <Paper p="md" radius="sm" withBorder>
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Recent Observations</Text>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/hotwash/observations')}
                >
                  View All
                </Button>
              </Group>
              {recent.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  No observations yet. Submit one to get started.
                </Text>
              ) : (
                <Stack gap="xs">
                  {recent.map(obs => {
                    const meta = TIER_META[obs.tier as keyof typeof TIER_META] ?? TIER_META['observation'];
                    return (
                      <Paper
                        key={obs.id} p="sm" radius="sm" withBorder
                        style={{ borderLeft: `3px solid ${meta.color}`, cursor: 'pointer' }}
                        onClick={() => { setSelectedObs(obs); openObsModal(); }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Group gap="xs" mb={2}>
                              <Badge size="xs" style={{ background: meta.bg, color: meta.color }}>{meta.label}</Badge>
                              <Badge size="xs" variant="outline" color="gray" tt="capitalize">{obs.category}</Badge>
                            </Group>
                            <Text size="sm" fw={500} lineClamp={1}>{obs.title}</Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>{obs.description}</Text>
                          </div>
                          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                            {obs.submittedBy}
                          </Text>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Paper>

          </SimpleGrid>
        </>
      )}

      {/* Observation Detail Modal */}
      <ObservationModal
        observation={selectedObs}
        opened={obsModalOpened}
        onClose={closeObsModal}
        onDelete={(id) => deleteObservation(id)}
      />
    </div>
  );
}
